import { useApiClient } from "@/src/hooks/useApiClient";
import { useQueryErrorToast } from "@/src/hooks/useQueryErrorToast";
import { useTranslations } from "@/src/i18n/i18n";
import { PAGE_SIZE } from "@/src/pages/Accounts/accounts.type";
import type { AccountFilters } from "@/src/pages/Accounts/hooks/useAccountFilters";

/**
 * La liste des comptes, filtrée et paginée.
 *
 * ⚠️ **`lat`, `lng` et `radius` partent ensemble ou pas du tout.** Le serveur
 * refuse (400) un rayon sans centre, et il a raison : une requête acceptée mais
 * non appliquée rendrait une liste plus large que demandé, sans que rien ne le
 * signale. `useAccountFilters` garantit déjà l'appariement, on ne fait ici que
 * ne pas le défaire.
 *
 * `total` est un `count(*)` sur le filtre : il donne le nombre de pages, et ne
 * se compare pas à `accounts.length`.
 */
export function useAccounts(filters: AccountFilters) {
  const $api = useApiClient();
  const t = useTranslations("accounts");

  const hasCenter = filters.lat !== null && filters.lng !== null;

  const { data, error, isError, isFetching } = $api.useQuery(
    "get",
    "/api/v1/moderation/accounts",
    {
      params: {
        query: {
          page: filters.page,
          limit: PAGE_SIZE,
          sort: filters.sort,
          order: filters.order,
          ...(filters.q.trim() ? { q: filters.q.trim() } : {}),
          ...(hasCenter
            ? {
                lat: filters.lat ?? undefined,
                lng: filters.lng ?? undefined,
                radius: filters.radius,
              }
            : {}),
        },
      },
    },
  );

  useQueryErrorToast(isError, t("list-error"), error);

  const total = data?.data?.total ?? 0;

  return {
    accounts: data?.data?.accounts ?? [],
    total,
    pageCount: Math.max(1, Math.ceil(total / PAGE_SIZE)),
    // `isFetching` et non `isLoading` : en changeant de page, le cache de la
    // précédente rend `isLoading` faux et l'écran afficherait l'ancienne page
    // sans dire qu'il travaille.
    isLoading: isFetching,
    isError,
  };
}
